import {
    Autocomplete,
    Box,
    Button,
    IconButton,
    Pagination,
    Paper,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import CertificateItem from '@src/App/Components/Items/EntityItems/CertificateItem/CertificateItem';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type Employee from '@src/Objects/Employee';
import type { AppState } from '@store/store';
import {
    Award,
    CheckCircle,
    Clock,
    Columns2,
    Grid3X3,
    List,
    Plus,
    Rows2,
    Settings,
    ShieldCheck,
    Target,
    TrendingUp,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CertificatesList from './components/CertificatesList';
import ExpiringCertificatesAlert from './components/ExpiringCertificatesAlert';
import LatestCertificateCard from './components/LatestCertificateCard';
import QuickLinksSection, { type QuickLinkTile } from './components/QuickLinksSection';
import StatsCards, { type StatsTile } from './components/StatsCards';
import T4loAssistantInfoBox from './components/T4loAssistantInfoBox';

interface MyCertificatesTabProps {
    employee: Employee;
}

function MyCertificatesTab({ employee }: MyCertificatesTabProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [searchValue, setSearchValue] = useState('');
    const [viewMode, setViewMode] = useState<'list' | 'cards'>('list');
    const [layoutMode, setLayoutMode] = useState<'2-column' | '1-column'>('2-column');
    const [page, setPage] = useState(1);
    const itemsPerPage = 5;

    const currentLayout = isMobile ? '1-column' : layoutMode;

    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).certificates;

    const allCertificates = useSelector((state: AppState) => state.company.allCertificates || []);

    const filteredCertificates = useMemo(() => {
        const certificates = employee.certificates || [];
        if (!searchValue) return certificates;
        return certificates.filter((cert) => cert.title.toLowerCase().includes(searchValue.toLowerCase()));
    }, [employee.certificates, searchValue]);

    const paginatedCertificates = useMemo(() => {
        const startIndex = (page - 1) * itemsPerPage;
        return filteredCertificates.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredCertificates, page, itemsPerPage]);

    const totalPages = Math.ceil(filteredCertificates.length / itemsPerPage);

    // Reset page when search changes
    React.useEffect(() => {
        setPage(1);
    }, [searchValue]);

    const autocompleteOptions = useMemo(() => {
        return allCertificates.map((cert) => cert.title);
    }, [allCertificates]);

    const employeeCertificates = employee.certificates || [];
    const now = new Date();
    const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);

    const certificateStats = {
        total: employeeCertificates.length,
        completedThisQuarter: employeeCertificates.filter((cert) => cert.startDate && cert.startDate >= quarterStart)
            .length,
        active: employeeCertificates.filter((cert) => !cert.endDate || cert.endDate > now).length,
        expiringSoon: employeeCertificates.filter((cert) => {
            if (!cert.endDate) return false;
            const twoMonthsFromNow = new Date();
            twoMonthsFromNow.setMonth(twoMonthsFromNow.getMonth() + 2);
            return cert.endDate <= twoMonthsFromNow && cert.endDate > now;
        }).length,
    };

    const quickLinkTiles: QuickLinkTile[] = [
        {
            title: 'Zertifikat hinzufügen',
            caption: 'Neue Qualifikation',
            icon: <Plus size={24} strokeWidth={1.5} />,
            color: theme.palette.primary.main,
            bgColor: alpha(theme.palette.primary.main, 0.1),
            borderColor: alpha(theme.palette.primary.main, 0.3),
            onClick: () => navigate(`/${ELinks.PROFILE_EDIT_CERTIFICATES}`),
        },
        {
            title: 'Entwicklungsziel',
            caption: 'Neues Ziel setzen',
            icon: <Target size={24} strokeWidth={1.5} />,
            color: theme.palette.primary.main,
            bgColor: alpha(theme.palette.primary.main, 0.1),
            borderColor: alpha(theme.palette.primary.main, 0.3),
            onClick: () => navigate(`/${ELinks.PROFILE_EDIT_DEVELOPMENT_NEW}`),
        },
    ];

    const tiles: StatsTile[] = [
        {
            title: 'Zertifikate',
            value: certificateStats.total.toString(),
            icon: <Award size={32} strokeWidth={1.5} />,
            color: theme.palette.primary.main,
            bgColor: theme.palette.tileBackground?.primary || `${theme.palette.primary.main}15`,
            borderColor: theme.palette.tileBorder?.primary || `${theme.palette.primary.main}90`,
        },
        {
            title: 'Aktiv',
            value: certificateStats.active.toString(),
            icon: <CheckCircle size={32} strokeWidth={1.5} />,
            color: '#0FC89D',
            bgColor: '#0FC89D15',
            borderColor: '#0FC89D90',
        },
        {
            title: 'Laufen ab',
            value: certificateStats.expiringSoon.toString(),
            icon: <Clock size={32} strokeWidth={1.5} />,
            color: '#EA4E42',
            bgColor: '#EA4E4215',
            borderColor: '#EA4E4290',
        },
    ];

    const MissionCard = () => (
        <Box
            sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                mb: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                    }}
                >
                    <Target size={20} />
                </Box>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        Nächste Lernpfad Mission
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        AWS Cloud Practitioner Lernpfad
                    </Typography>
                </Box>
            </Box>

            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                AWS Certified Solutions Architect
            </Typography>

            <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
                Erweitere deine Cloud-Kenntnisse und werde zum Architektur-Experten. Dieses Zertifikat ist der nächste
                Schritt in deinem AWS-Lernpfad.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                    size="small"
                    variant="outlined"
                    sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        fontWeight: 500,
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                    }}
                >
                    Lernpfad ansehen
                </Button>

                <Button
                    size="small"
                    variant="contained"
                    sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        fontWeight: 500,
                    }}
                >
                    Mission starten
                </Button>
            </Box>
        </Box>
    );

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        Zertifikate
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Verwalte deine Zertifikate und Qualifikationen
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={() => navigate(`/${ELinks.PROFILE_EDIT_CERTIFICATES}`)}
                        aria-label="Zertifikate bearbeiten"
                        title="Zertifikate bearbeiten"
                        sx={{
                            color: theme.palette.primary.main,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                            },
                        }}
                    >
                        {getAppIcon(EIconCategory.EDIT)}
                    </IconButton>

                    {/* View Toggle */}
                    {!isMobile && (
                        <Box
                            sx={{
                                display: 'flex',
                                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                borderRadius: 1,
                                ml: 1,
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={() => setLayoutMode('2-column')}
                                sx={{
                                    color:
                                        layoutMode === '2-column'
                                            ? theme.palette.primary.main
                                            : theme.palette.text.secondary,
                                    bgcolor:
                                        layoutMode === '2-column'
                                            ? alpha(theme.palette.primary.main, 0.1)
                                            : 'transparent',
                                    borderRadius: '4px 0 0 4px',
                                }}
                            >
                                <Columns2 size={16} />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => setLayoutMode('1-column')}
                                sx={{
                                    color:
                                        layoutMode === '1-column'
                                            ? theme.palette.primary.main
                                            : theme.palette.text.secondary,
                                    bgcolor:
                                        layoutMode === '1-column'
                                            ? alpha(theme.palette.primary.main, 0.1)
                                            : 'transparent',
                                    borderRadius: '0 4px 4px 0',
                                }}
                            >
                                <Rows2 size={16} />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Content Layout */}
            {currentLayout === '2-column' ? (
                <Box sx={{ display: 'flex', gap: 3 }}>
                    {/* Left Column */}
                    <Box sx={{ flex: '1 1 60%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <StatsCards tiles={tiles} />

                        {/* Overview Section */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Meine Zertifikate
                                </Typography>
                            </Box>

                            {/* Search and View Controls */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Autocomplete
                                    freeSolo
                                    options={autocompleteOptions}
                                    value={searchValue}
                                    onInputChange={(_, newValue) => setSearchValue(newValue || '')}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Zertifikat suchen..."
                                            size="small"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                    '& fieldset': {
                                                        borderColor: theme.palette.divider,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: theme.palette.primary.main,
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: theme.palette.primary.main,
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                    sx={{ flex: 1 }}
                                />

                                <IconButton
                                    size="small"
                                    onClick={() => setViewMode('cards')}
                                    sx={{
                                        color:
                                            viewMode === 'cards'
                                                ? theme.palette.primary.main
                                                : theme.palette.text.secondary,
                                        bgcolor:
                                            viewMode === 'cards' ? `${theme.palette.primary.main}15` : 'transparent',
                                    }}
                                >
                                    <Grid3X3 size={16} />
                                </IconButton>

                                <IconButton
                                    size="small"
                                    onClick={() => setViewMode('list')}
                                    sx={{
                                        color:
                                            viewMode === 'list'
                                                ? theme.palette.primary.main
                                                : theme.palette.text.secondary,
                                        bgcolor:
                                            viewMode === 'list' ? `${theme.palette.primary.main}15` : 'transparent',
                                    }}
                                >
                                    <List size={16} />
                                </IconButton>
                            </Box>

                            {filteredCertificates.length > 0 ? (
                                viewMode === 'list' ? (
                                    <Box>
                                        {paginatedCertificates.map((certificate) => (
                                            <CertificateItem
                                                key={certificate.id}
                                                id={certificate.id}
                                                title={certificate.title}
                                                startDate={certificate.startDate}
                                                endDate={certificate.endDate}
                                            />
                                        ))}
                                    </Box>
                                ) : (
                                    <CertificatesList certificates={paginatedCertificates} viewMode={viewMode} />
                                )
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <ShieldCheck
                                        size={48}
                                        style={{ color: theme.palette.text.secondary, marginBottom: '16px' }}
                                    />
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        Keine Zertifikate vorhanden
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Füge deine ersten Zertifikate hinzu, um sie hier zu sehen
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<Plus />}
                                        onClick={() => navigate(`/${ELinks.PROFILE_EDIT_CERTIFICATES}`)}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Zertifikate hinzufügen
                                    </Button>
                                </Box>
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={(_, newPage) => setPage(newPage)}
                                        size="small"
                                        color="primary"
                                    />
                                </Box>
                            )}
                        </Paper>
                    </Box>

                    {/* Right Column */}
                    <Box sx={{ flex: '1 1 40%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <T4loAssistantInfoBox context="certificates" />
                        <MissionCard />
                        <LatestCertificateCard employee={employee} />
                        <ExpiringCertificatesAlert employee={employee} />
                        <QuickLinksSection tiles={quickLinkTiles} layout="1-column" />
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <T4loAssistantInfoBox context="certificates" />
                    <StatsCards tiles={tiles} />

                    {/* Overview Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            bgcolor: 'background.paper',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Meine Zertifikate
                            </Typography>
                        </Box>

                        {/* Search and View Controls */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Autocomplete
                                freeSolo
                                options={autocompleteOptions}
                                value={searchValue}
                                onInputChange={(_, newValue) => setSearchValue(newValue || '')}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Zertifikat suchen..."
                                        size="small"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                '& fieldset': {
                                                    borderColor: theme.palette.divider,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.palette.primary.main,
                                                },
                                            },
                                        }}
                                    />
                                )}
                                sx={{ flex: 1 }}
                            />

                            <IconButton
                                size="small"
                                onClick={() => setViewMode('cards')}
                                sx={{
                                    color:
                                        viewMode === 'cards'
                                            ? theme.palette.primary.main
                                            : theme.palette.text.secondary,
                                    bgcolor: viewMode === 'cards' ? `${theme.palette.primary.main}15` : 'transparent',
                                }}
                            >
                                <Grid3X3 size={16} />
                            </IconButton>

                            <IconButton
                                size="small"
                                onClick={() => setViewMode('list')}
                                sx={{
                                    color:
                                        viewMode === 'list' ? theme.palette.primary.main : theme.palette.text.secondary,
                                    bgcolor: viewMode === 'list' ? `${theme.palette.primary.main}15` : 'transparent',
                                }}
                            >
                                <List size={16} />
                            </IconButton>
                        </Box>

                        {filteredCertificates.length > 0 ? (
                            viewMode === 'list' ? (
                                <Box>
                                    {paginatedCertificates.map((certificate) => (
                                        <CertificateItem
                                            key={certificate.id}
                                            id={certificate.id}
                                            title={certificate.title}
                                            startDate={certificate.startDate}
                                            endDate={certificate.endDate}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <CertificatesList certificates={paginatedCertificates} viewMode={viewMode} />
                            )
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <ShieldCheck
                                    size={48}
                                    style={{ color: theme.palette.text.secondary, marginBottom: '16px' }}
                                />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    Keine Zertifikate vorhanden
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Füge deine ersten Zertifikate hinzu, um sie hier zu sehen
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<Plus />}
                                    onClick={() => navigate(`/${ELinks.PROFILE_EDIT_CERTIFICATES}`)}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Zertifikate hinzufügen
                                </Button>
                            </Box>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(_, newPage) => setPage(newPage)}
                                    size="small"
                                    color="primary"
                                />
                            </Box>
                        )}
                    </Paper>

                    <MissionCard />
                    <LatestCertificateCard employee={employee} />
                    <ExpiringCertificatesAlert employee={employee} />
                </Box>
            )}
        </Box>
    );
}

export default MyCertificatesTab;
